import { useEffect, useMemo, useState } from "react";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";
import type { SignatureStepDTO } from "@platform/shared-types";

export type SignatureActionSigner = {
  id: string;
  fullName: string;
  phone: string;
  nationalId?: string;
};

export type SignatureActionResult = {
  contractId: string;
  mode: "in_platform" | "gateway" | "samt_root_cert";
  processId?: string;
  signers: SignatureStepDTO[];
};

type Props = {
  open: boolean;
  fileName: string;
  busy?: boolean;
  error?: string | null;
  result?: SignatureActionResult | null;
  selfName?: string;
  selfPhone?: string | null;
  onClose: () => void;
  onSelfOnly: () => void;
  onSendToOthers: (input: {
    includeSelf: boolean;
    notifySms: boolean;
    redirectUrl?: string;
    failureRedirectUrl?: string;
    signers: SignatureActionSigner[];
  }) => void;
};

const newSigner = (): SignatureActionSigner => ({
  id: crypto.randomUUID(),
  fullName: "",
  phone: "",
  nationalId: "",
});

function absoluteGatewayUrl(link: string | null | undefined): string | null {
  if (!link) return null;
  if (/^https?:\/\//i.test(link)) return link;
  if (typeof window === "undefined") return link;
  return `${window.location.origin}${link.startsWith("/") ? "" : "/"}${link}`;
}

function signerLabel(s: SignatureStepDTO): string {
  return s.representativeName || s.displayName || s.partyName || s.phone || s.userId;
}

export function SignatureActionSheet({
  open,
  fileName,
  busy = false,
  error,
  result,
  selfName = "من",
  selfPhone,
  onClose,
  onSelfOnly,
  onSendToOthers,
}: Props) {
  const [mode, setMode] = useState<"self" | "others">("self");
  const [includeSelf, setIncludeSelf] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [failureRedirectUrl, setFailureRedirectUrl] = useState("");
  const [signers, setSigners] = useState<SignatureActionSigner[]>(() => [newSigner()]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode("self");
    setIncludeSelf(true);
    setNotifySms(false);
    setRedirectUrl("");
    setFailureRedirectUrl("");
    setSigners([newSigner()]);
    setCopied(null);
  }, [open, fileName]);

  const cleanedSigners = useMemo(
    () =>
      signers
        .map((s) => ({
          ...s,
          fullName: s.fullName.trim(),
          phone: s.phone.trim(),
          nationalId: s.nationalId?.trim(),
        }))
        .filter((s) => s.fullName || s.phone || s.nationalId),
    [signers],
  );

  if (!open) return null;

  const canSendOthers =
    !busy &&
    cleanedSigners.length > 0 &&
    cleanedSigners.every((s) => s.fullName.length >= 2 && s.phone.length >= 8);

  const gatewayLinks =
    result?.signers
      .map((s) => ({ signer: s, link: absoluteGatewayUrl(s.gatewayLink) }))
      .filter((x) => x.link) ?? [];

  async function copy(text: string) {
    await navigator.clipboard?.writeText(text);
    setCopied(text);
    window.setTimeout(() => setCopied(null), 1400);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <div
        className="sheet-card signature-action-sheet"
        role="dialog"
        aria-label="امضای سند"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head signature-action-head">
          <div>
            <h2>امضای سند</h2>
            <p>{fileName}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="بستن">
            ×
          </button>
        </header>

        <div className="signature-choice-grid" role="radiogroup" aria-label="نوع امضا">
          <button
            type="button"
            role="radio"
            aria-checked={mode === "self"}
            className={`signature-choice${mode === "self" ? " on" : ""}`}
            disabled={busy}
            onClick={() => setMode("self")}
          >
            <strong>فقط خودم امضا کنم</strong>
            <span>ورود به جریان امضای خودتان برای همین سند</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === "others"}
            className={`signature-choice${mode === "others" ? " on" : ""}`}
            disabled={busy}
            onClick={() => setMode("others")}
          >
            <strong>دیگران را هم اضافه کنم</strong>
            <span>ساخت لینک درگاه برای امضاکنندگان</span>
          </button>
        </div>

        {mode === "self" ? (
          <section className="signature-action-section">
            <p className="settings-hint">
              اگر پروفایل/گواهی امضای شما آماده باشد، باز کردن قفل و ثبت امضا نمایش داده می‌شود؛
              در غیر این صورت جریان احراز هویت درگاه امضا شروع می‌شود.
            </p>
            <button
              type="button"
              className="primary-btn signature-wide-btn"
              disabled={busy}
              onClick={onSelfOnly}
            >
              {busy ? <Loader2 size={16} className="spin" /> : null}
              شروع امضای خودم
            </button>
          </section>
        ) : (
          <section className="signature-action-section">
            <label className="signature-toggle">
              <input
                type="checkbox"
                checked={includeSelf}
                disabled={busy}
                onChange={(e) => setIncludeSelf(e.target.checked)}
              />
              <span>من هم امضاکننده هستم</span>
              <small>{selfPhone ? `${selfName} · ${selfPhone}` : selfName}</small>
            </label>

            <div className="signature-signers-list">
              {signers.map((signer, index) => (
                <div className="signature-signer-card" key={signer.id}>
                  <div className="signature-signer-head">
                    <strong>امضاکننده {index + 1}</strong>
                    {signers.length > 1 ? (
                      <button
                        type="button"
                        className="icon-btn compact"
                        disabled={busy}
                        onClick={() => setSigners((cur) => cur.filter((s) => s.id !== signer.id))}
                        aria-label="حذف امضاکننده"
                      >
                        <Trash2 size={15} />
                      </button>
                    ) : null}
                  </div>
                  <label>
                    نام و نام خانوادگی
                    <input
                      value={signer.fullName}
                      disabled={busy}
                      onChange={(e) =>
                        setSigners((cur) =>
                          cur.map((s) =>
                            s.id === signer.id ? { ...s, fullName: e.target.value } : s,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    موبایل
                    <input
                      dir="ltr"
                      inputMode="tel"
                      value={signer.phone}
                      disabled={busy}
                      onChange={(e) =>
                        setSigners((cur) =>
                          cur.map((s) =>
                            s.id === signer.id ? { ...s, phone: e.target.value } : s,
                          ),
                        )
                      }
                    />
                  </label>
                  <label>
                    کد ملی (اختیاری)
                    <input
                      dir="ltr"
                      inputMode="numeric"
                      value={signer.nationalId ?? ""}
                      disabled={busy}
                      onChange={(e) =>
                        setSigners((cur) =>
                          cur.map((s) =>
                            s.id === signer.id ? { ...s, nationalId: e.target.value } : s,
                          ),
                        )
                      }
                    />
                  </label>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="ghost-btn signature-wide-btn"
              disabled={busy}
              onClick={() => setSigners((cur) => [...cur, newSigner()])}
            >
              <Plus size={15} />
              افزودن امضاکننده
            </button>

            <div className="signature-advanced">
              <label className="signature-toggle">
                <input
                  type="checkbox"
                  checked={notifySms}
                  disabled={busy}
                  onChange={(e) => setNotifySms(e.target.checked)}
                />
                <span>ارسال پیامک توسط نویساین</span>
                <small>پیش‌فرض خاموش است؛ لینک‌ها بعد از ارسال نمایش داده می‌شوند.</small>
              </label>
              <label>
                آدرس بازگشت موفق (اختیاری)
                <input
                  dir="ltr"
                  value={redirectUrl}
                  disabled={busy}
                  onChange={(e) => setRedirectUrl(e.target.value)}
                  placeholder="https://..."
                />
              </label>
              <label>
                آدرس بازگشت ناموفق (اختیاری)
                <input
                  dir="ltr"
                  value={failureRedirectUrl}
                  disabled={busy}
                  onChange={(e) => setFailureRedirectUrl(e.target.value)}
                  placeholder="https://..."
                />
              </label>
            </div>

            <button
              type="button"
              className="primary-btn signature-wide-btn"
              disabled={!canSendOthers}
              onClick={() =>
                onSendToOthers({
                  includeSelf,
                  notifySms,
                  redirectUrl: redirectUrl.trim() || undefined,
                  failureRedirectUrl: failureRedirectUrl.trim() || undefined,
                  signers: cleanedSigners,
                })
              }
            >
              {busy ? <Loader2 size={16} className="spin" /> : null}
              ارسال برای امضا
            </button>
          </section>
        )}

        {error ? <p className="reader-login-error">{error}</p> : null}

        {result ? (
          <section className="signature-result-card">
            <strong>فرایند امضا ساخته شد</strong>
            <span>شناسه سند: {result.contractId}</span>
            {gatewayLinks.length ? (
              <ul>
                {gatewayLinks.map(({ signer, link }) => (
                  <li key={`${signer.userId}-${signer.order}`}>
                    <span>{signerLabel(signer)}</span>
                    <button type="button" onClick={() => link && void copy(link)}>
                      <Copy size={13} />
                      {copied === link ? "کپی شد" : "کپی لینک"}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>اگر نوبت امضای شما باشد، پنل امضا باز می‌شود.</p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
